import numeral from 'numeral';
import { ColumnFormat } from '/@/modules/core/app/constants/data.constants';
import Locale from '/@/modules/user/Enum/Locale';
import Lang from '/@/modules/core/app/services/Lang';
import { NumeralFormat } from '@/modules/core/app/constants/NumeralFormat';

class NumeralService {
  constructor() {
    numeral.register('locale', this.getShortLocale(Locale.FRENCH.key), {
      delimiters: {
        thousands: ' ',
        decimal: ','
      },
      abbreviations: {
        thousand: 'k',
        million: 'm',
        billion: 'b',
        trillion: 't'
      },
      currency: {
        symbol: '$'
      }
    });
  }

  /**
   * @param value
   * @param format
   * @param precision
   * @param isCompactNumber
   * @param currencySymbol
   * @param hasFixedDecimals
   * @param hasBracketsForNegativeValue
   * @returns {string}
   */
  formatValue(
    value,
    format,
    precision = 0,
    isCompactNumber = false,
    currencySymbol = '$',
    hasFixedDecimals = false,
    hasBracketsForNegativeValue = false
  ) {
    if (value < 1000) {
      isCompactNumber = false;
    }

    const precisionFormat = this.getPrecisionFormat(isCompactNumber ? 2 : precision);
    const compactNumberFormat = isCompactNumber ? 'a' : '';
    // hasFixedDecimals = true will return 1000.00 for 1000
    // hasFixedDecimals = false will return 1000 for 1000
    let numeralFormat = `0,0${
      hasFixedDecimals ? '.' : '[.]'
    }${precisionFormat}${compactNumberFormat}`;

    if (format === ColumnFormat.FORMAT_PERCENT) {
      numeralFormat = `${numeralFormat}%`;
      // Since NumeralService converts 2 to 200%.
      value = value ? Number(value) / 100 : value;
    }

    if (hasBracketsForNegativeValue && value < 0) {
      numeralFormat = `(${numeralFormat})`;
    }

    numeral.nullFormat(NumeralFormat.NULL);

    if (format === ColumnFormat.FORMAT_CURRENCY) {
      numeralFormat = Lang.isFrench()
        ? `${numeralFormat} ${currencySymbol}`
        : `${currencySymbol}${numeralFormat}`;
    }

    return numeral(value).format(numeralFormat);
  }

  /**
   * @param value
   * @param {Column} column
   * @param isCompactNumber
   */
  formatColumnValue(value, column, isCompactNumber = false) {
    return this.formatValue(
      value,
      column.format,
      column.precision,
      isCompactNumber,
      column.currencySymbol
    );
  }

  /**
   * @param precision
   * @returns {string|string}
   * @private
   */
  getPrecisionFormat(precision) {
    let format = '';
    for (let i = 0; i < precision; i++) {
      format += '0';
    }
    return format;
  }

  /**
   * @param value
   * @param comparisonValue
   * @returns {number}
   */
  calculateDelta(value, comparisonValue) {
    value = Number(value);
    comparisonValue = Number(comparisonValue);
    return value !== 0 ? ((value - comparisonValue) / Math.abs(value)) * 100 : 0;
  }

  /**
   * @param value
   * @returns {number}
   */
  getAbsoluteValue(value) {
    return Math.abs(value) || 0;
  }

  /**
   * @param locale
   */
  load(locale) {
    numeral.locale(this.getShortLocale(locale));
  }

  getShortLocale(locale) {
    return locale.substring(0, 2);
  }
}

export default new NumeralService();
